import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import type { ColumnMeta } from "../constants/table.cnst";
import {
  Paginator,
  type PaginatorPageChangeEvent
} from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";

interface TableProps {
  columns: ColumnMeta[];
  data: any[];
  showGridlines?: boolean;
  paginator?: boolean;
  hasSNo?: boolean;
  id?: string;

  renderCell?: (
    row: any,
    colKey: string,
    rowIndex: number,
    path?: string,
    expansion?: {
      isExpanded: boolean;
      toggle: () => void;
    }
  ) => React.ReactNode;

  onPageChangeExternal?: (
    page_num: number,
    total_entry_per_page: number
  ) => void;

  totalRecords?: number;
  isLoading?: boolean;
  first: number;
  rows: number;
  enableScroll?: boolean;

  // Row expansion configuration
  rowExpansion?: {
    key: string;
    render: (rowData: any) => React.ReactNode;
  };
  expandAllRows?: boolean;
  expansionRequest?: number;
  onExpansionStateChange?: (allExpanded: boolean, allCollapsed: boolean) => void;

  isHighlight?: boolean;
}

export default function Table({
  columns,
  data,
  showGridlines,
  paginator,
  hasSNo,
  renderCell,
  onPageChangeExternal,
  totalRecords,
  isLoading,
  first,
  rows,
  enableScroll = false,
  rowExpansion,
  expandAllRows,
  expansionRequest,
  onExpansionStateChange,
  id,
  isHighlight = false,
}: TableProps) {
  const [expandedRows, setExpandedRows] = useState<any[]>([]);

  // const [first, setFirst] = useState<number>(0);
  // const [rows, setRows] = useState<number>(10);

  // const [globalFilter, setGlobalFilter] = useState<string>("");
  // const [filters, setFilters] = useState<{
  //   [key: string]: string;
  // }>({});
  // console.log(setGlobalFilter);
  // console.log(setFilters);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    // setFirst(event.first);
    // setRows(event.rows);

    const pageNum = Math.floor(event.first / event.rows) + 1;

    onPageChangeExternal?.(pageNum, event.rows);
  };

  const DATE_FIELDS = ["tripStart", "tripEnd", "date", "timestamp", "event_timestamp"];

  const parseDateValue = (value: unknown): number | null => {
    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value !== "string" || !value.trim()) {
      return null;
    }

    const [datePart = "", timePart = ""] = value
      .trim()
      .split(",");

    const [day, month, year] = datePart
      .trim()
      .split("/")
      .map(Number);

    if (!day || !month || !year) {
      return null;
    }

    const timeParts = timePart
      .trim()
      .split(":")
      .map(Number);

    const hours = timeParts[0] ?? 0;
    const minutes = timeParts[1] ?? 0;
    const seconds = timeParts[2] ?? 0;
    const milliseconds = timeParts[3] ?? 0;

    const timestamp = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      seconds,
      milliseconds
    ).getTime();

    return Number.isNaN(timestamp) ? null : timestamp;
  };

  // 🔍 Apply global + column filters
  // const filteredData = data.filter((item) => {
  //   // global search
  //   const globalMatch = Object.values(item)
  //     .join(" ")
  //     .toLowerCase()
  //     .includes(globalFilter.toLowerCase());

  //   // per-column filters
  //   const colMatch = Object.keys(filters).every((key) => {
  //     if (!filters[key]) return true;
  //     return String(item[key])
  //       .toLowerCase()
  //       .includes(filters[key].toLowerCase());
  //   });

  //   return globalMatch && colMatch;
  // });

  const filteredData = useMemo(
    () =>
      data.map((row: any) => ({
        ...row,
        tripStartSort: parseDateValue(row.tripStart),
        tripEndSort: parseDateValue(row.tripEnd),
        dateSort: parseDateValue(row.date),
        timestampSort: parseDateValue(row.timestamp),
        event_timestampSort: parseDateValue(row.event_timestamp),
      })),
    [data]
  );

  const expansionDataRef = useRef({ filteredData, rowExpansion });
  expansionDataRef.current = { filteredData, rowExpansion };
  const rowIdentity = rowExpansion
    ? filteredData.map((row) => row[rowExpansion.key]).join("|")
    : "";

  useEffect(() => {
    const { filteredData: currentData, rowExpansion: currentExpansion } = expansionDataRef.current;
    if (currentExpansion && currentData.length > 0) {
      setExpandedRows(expandAllRows === undefined || expandAllRows ? currentData : []);
    } else {
      setExpandedRows([]);
    }
  }, [rowIdentity, expandAllRows, expansionRequest]);

  useEffect(() => {
    if (!rowExpansion || filteredData.length === 0) {
      onExpansionStateChange?.(false, true);
      return;
    }

    const expandedCount = filteredData.filter((row) =>
      expandedRows.some(
        (expandedRow) =>
          expandedRow[rowExpansion.key] === row[rowExpansion.key]
      )
    ).length;
    onExpansionStateChange?.(
      expandedCount === filteredData.length,
      expandedCount === 0
    );
  }, [expandedRows, filteredData, rowExpansion, onExpansionStateChange]);

  // 🔥 Final sliced data for external paginator
  // const paginatedData = filteredData.slice(first, first + rows);

  const skeletonRows = Array.from({ length: rows }).map((_, i) => {
    const row: any = {};

    columns.forEach((col) => {
      row[col.field] = null;
    });

    row.__skeletonId = `skeleton-${i}`;

    if (rowExpansion?.key) {
      row[rowExpansion.key] = `skeleton-${i}`;
    }

    return row;
  });

  return (
    <div className="card">
      {/* GLOBAL SEARCH */}
      {/* <div className="flex justify-end mb-2">
        <InputText
          value={globalFilter}
          onChange={(e) => {
            setGlobalFilter(e.target.value);
            setFirst(0); // reset page
          }}
          placeholder="Search..."
          className="p-inputtext-sm"
        />
      </div> */}

      <DataTable
        id={id}
        className={isHighlight ? "highlight-table" : ""}
        value={isLoading ? skeletonRows : filteredData}
        key={filteredData?.length}
        dataKey={rowExpansion?.key}
        showGridlines={showGridlines}
        tableStyle={{ minWidth: "50rem" }}
        sortMode="single"
        scrollable={enableScroll}
        scrollHeight={enableScroll ? "450px" : undefined}
        removableSort

        // Row expansion
        expandedRows={expandedRows}
        rowExpansionTemplate={
          rowExpansion
            ? (rowData) => rowExpansion.render(rowData)
            : undefined
        }

        emptyMessage={
          <div className="text-center text-gray-500 py-6">
            No Data Available
          </div>
        }

      // filterDisplay="row"
      >
        {hasSNo && (
          <Column
            header="S.No"
            body={(_, { rowIndex }) =>
              isLoading ? (
                <Skeleton width="2rem" height="1rem" />
              ) : (
                first + rowIndex + 1
              )
            }
            headerStyle={{
              position: "sticky",
              top: 0,
              zIndex: 1
            }}
          />
        )}

        {columns.map((col) => {
          const isDateField = DATE_FIELDS.includes(col.field);

          return (

            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              sortable={col.hasSort}
              sortField={isDateField ? `${col.field}Sort` : undefined}
              headerStyle={{
                position: "sticky",
                top: "0",
                zIndex: 1,
                ...(col.header === "Health Module" ? { width: "15rem" } : {})
              }}

              // filter={col.hasMultiColumnFilter}

              // filterElement={
              //   <InputText
              //     value={filters[col.field] || ""}
              //     onChange={(e) => {
              //       setFilters((prev) => ({
              //         ...prev,
              //         [col.field]: e.target.value
              //       }));
              //       setFirst(0);
              //     }}
              //     className="p-inputtext-sm"
              //   />
              // }

              // showFilterMenu={false}

              // filterMatchMode="contains"

              body={(rowData, { rowIndex }) =>
                isLoading ? (
                  <Skeleton
                    width="100%"
                    height="1.2rem"
                  />
                ) : col.templateName ? (
                  renderCell?.(
                    rowData,
                    col.field,
                    rowIndex,
                    col?.path,

                    // Pass expansion information to renderCell
                    rowExpansion
                      ? {
                        isExpanded: expandedRows.some(
                          (row) =>
                            row[rowExpansion.key] ===
                            rowData[rowExpansion.key]
                        ),

                        toggle: () => {
                          const rowKey =
                            rowData[rowExpansion.key];

                          setExpandedRows((prev) => {
                            const isExpanded = prev.some(
                              (row) =>
                                row[rowExpansion.key] ===
                                rowKey
                            );

                            if (isExpanded) {
                              return prev.filter(
                                (row) =>
                                  row[rowExpansion.key] !==
                                  rowKey
                              );
                            }

                            return [...prev, rowData];
                          });
                        }
                      }
                      : undefined
                  )
                ) : (
                  (rowData[col.field] ?? "") !== "" ? (
                    ["tripStart", "tripEnd", "timestamp"].includes(
                      col.field
                    ) ? (
                      (() => {
                        const [date, time] =
                          rowData[col.field].split(" ");

                        return (
                          <>
                            {date}
                            <br />
                            {time}
                          </>
                        );
                      })()
                    ) : (
                      rowData[col.field]
                    )
                  ) : (
                    "-"
                  )
                )
              }
            />
          )
        })}
      </DataTable>

      {paginator && (totalRecords as number) > 5 && (
        <div className="flex items-center justify-between px-2 py-2">
          <span className="text-sm font-medium">
            Showing {first + 1} to{" "}
            {Math.min(
              first + rows,
              totalRecords as number
            )}{" "}
            of {totalRecords}
          </span>

          <Paginator
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            onPageChange={onPageChange}
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}